/**
 * Mission 30's laboratory: a clean prefabricated block, as unlike the garrison's timber and adobe as
 * a building can be - pale steel panels on a concrete plinth, a flat roof crowded with vents, and a
 * long window lit a cold white where every other window in the game is lamp-yellow.
 */

import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { snowSlab } from './snow';

const FRONT = 2.8;
const PANE = { x: 1.3, y: 1.25, w: 2.8, h: 0.75 };

export const laboratory: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const mid = p.color('metal.mid');
  mb.box(8.0, 0.25, 6.0, { all: p.color('concrete.lo'), top: p.color('concrete.mid') });
  // Pale panels: the ramp's LIGHT step on the faces the camera sees, its mid step east and west.
  mb.box(7.6, 2.5, FRONT * 2, { all: p.color('metal.hi'), left: mid, right: mid }, place(0, 0.25, 0));
  // Panel joints on the front; the door with the enemy's board over it.
  for (const x of [-1.9, -0.3, 3.0]) mb.box(0.1, 2.5, 0.05, p.color('metal.mid'), place(x, 0.25, FRONT + 0.02));
  mb.box(1.0, 1.9, 0.08, p.color('ink.1'), place(-2.8, 0.25, FRONT + 0.02));
  mb.box(1.1, 0.2, 0.08, p.color('enemyAccent.0'), place(-2.8, 2.2, FRONT + 0.03));
  // The lit window, two mullions and a sill; a dark one beside the door.
  mb.box(PANE.w, PANE.h, 0.08, p.color('fire.3'), place(PANE.x, PANE.y, FRONT + 0.02));
  for (const dx of [-0.47, 0.47]) mb.box(0.08, PANE.h, 0.1, mid, place(PANE.x + dx, PANE.y, FRONT + 0.03));
  mb.box(PANE.w + 0.2, 0.1, 0.16, p.color('metal.mid'), place(PANE.x, PANE.y - 0.08, FRONT + 0.05));
  mb.box(0.9, PANE.h, 0.08, p.color('ink.1'), place(-1.1, PANE.y, FRONT + 0.02));

  // Roof slab and what stands on it: two cowls, an air handler with a dark grille, a fume stack.
  mb.box(8.0, 0.25, 6.0, { all: p.color('roof.0'), top: p.color('roof.1') }, place(0, 2.75, 0));
  snowSlab(mb, p, 8.0, 6.0, 3.0, 0.4);
  for (const x of [-2.6, -1.3]) mb.cylinder(0.3, 0.3, 0.5, 6, mid, place(x, 3.0, -1.6), p.color('ink.1'));
  mb.box(1.5, 0.6, 1.0, { all: p.color('metal.mid'), top: p.color('metal.hi') }, place(2.3, 3.0, -1.5));
  mb.box(1.1, 0.04, 0.6, p.color('ink.1'), place(2.3, 3.6, -1.5));
  mb.cylinder(0.12, 0.12, 1.3, 5, p.color('metal.lo'), place(3.3, 3.0, 1.7), p.color('ink.0'));
  return mb;
};

/** The lit pane alone, a shade proud of the wall's: the self-lit part. */
export const laboratoryGlow: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const lit = p.color('fire.3');
  for (const dx of [-0.94, 0, 0.94]) {
    mb.box(0.84, PANE.h - 0.04, 0.04, lit, place(PANE.x + dx, PANE.y + 0.02, FRONT + 0.06));
  }
  return mb;
};
